import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { hasAnyRole } from '@/modules/auth/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { useBranchStore } from '@/stores/branchStore';

const LAYOUT_BRANCHES_QUERY = gql`
  query LayoutBranches {
    branches {
      id
      name
    }
  }
`;

interface Branch {
  id: string;
  name: string;
}

/**
 * Administrator-only multi-branch oversight picker. Hidden for non-admins and when there is
 * only one branch. The selection feeds branch-aware list queries via useBranchScope().
 */
export function BranchSelector(): JSX.Element | null {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const selected = useBranchStore((s) => s.selected_branch_id);
  const setSelected = useBranchStore((s) => s.setSelectedBranch);
  const is_admin = Boolean(user && hasAnyRole(user.roles, ['administrator']));

  const { data } = useQuery<{ branches: Branch[] }>(LAYOUT_BRANCHES_QUERY, { skip: !is_admin });
  const branches = data?.branches ?? [];

  // Dormant in a single-branch deployment; self-activates once a second branch exists.
  if (!is_admin || branches.length <= 1) return null;

  return (
    <select
      aria-label={t('layout.branch_selector.aria_label')}
      value={selected ?? ''}
      onChange={(e) => setSelected(e.target.value || null)}
      className="border-input bg-background h-9 rounded-md border px-2 text-sm"
    >
      <option value="">{t('layout.branch_selector.all_branches')}</option>
      {branches.map((b) => (
        <option key={b.id} value={b.id}>
          {b.name}
        </option>
      ))}
    </select>
  );
}
